import React, { useState, useEffect } from "react";

interface CookiePreferences {
  analytics: boolean;
  functional: boolean;
  marketing: boolean;
  timestamp: number;
  version: number;
}

const DEFAULT_PREFERENCES: CookiePreferences = {
  analytics: false,
  functional: true,
  marketing: false,
  timestamp: Date.now(),
  version: 1,
};

export const CookieConsent: React.FC = () => {
  const [showConsent, setShowConsent] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] =
    useState<CookiePreferences>(DEFAULT_PREFERENCES);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("cookieConsent");
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as CookiePreferences;
        setPreferences(parsed);
        applyConsentChoices(parsed);
      } catch (e) {
        console.error("Failed to parse cookie consent", e);
        setShowConsent(true);
      }
    } else {
      setShowConsent(true);
    }
    setIsLoaded(true);
  }, []);

  const applyConsentChoices = (prefs: CookiePreferences) => {
    const gtag = (window as unknown as { gtag?: (command: string, consentType: string, config: Record<string, string>) => void }).gtag;
    if (prefs.analytics && gtag) {
      gtag("consent", "update", {
        analytics_storage: "granted",
      });
    } else if (!prefs.analytics && gtag) {
      gtag("consent", "update", {
        analytics_storage: "denied",
      });
    }

    (window as unknown as { cookieConsent?: CookiePreferences }).cookieConsent = prefs;

    window.dispatchEvent(
      new CustomEvent("cookieConsentUpdated", { detail: prefs })
    );
  };

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      ...DEFAULT_PREFERENCES,
      analytics: true,
      marketing: true,
      timestamp: Date.now(),
    };
    saveCookiePreferences(allAccepted);
  };

  const handleRejectAll = () => {
    const minimal: CookiePreferences = {
      ...DEFAULT_PREFERENCES,
      analytics: false,
      marketing: false,
      timestamp: Date.now(),
    };
    saveCookiePreferences(minimal);
  };

  const handleSavePreferences = () => {
    saveCookiePreferences(preferences);
  };

  const saveCookiePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem("cookieConsent", JSON.stringify(prefs));
    setPreferences(prefs);
    applyConsentChoices(prefs);
    setShowConsent(false);
    setShowSettings(false);
  };

  const handleOpenSettings = () => {
    setShowSettings(true);
  };

  if (!isLoaded) {
    return null;
  }

  return (
    <React.Fragment>
      {showConsent && !showSettings && (
        <div
          className="fixed bottom-0 left-0 right-0 bg-[var(--card-bg)] border-t border-[var(--border-color)] p-4 z-50"
          role="dialog"
          aria-label="Cookie Consent"
        >
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white mb-2">We respect your privacy</h3>
              <p className="text-sm text-muted">
                We use cookies to improve your experience. Some are essential
                for the site to function, while others help us understand how
                you use it so we can improve.
              </p>
            </div>

            <div className="flex flex-wrap gap-2 shrink-0">
              <button
                className="px-4 py-2 bg-transparent border border-[var(--border-color)] text-white rounded-lg text-sm font-medium hover:bg-white/5"
                onClick={handleRejectAll}
                aria-label="Reject all cookies"
              >
                Reject All
              </button>
              <button
                className="px-4 py-2 bg-transparent border border-[var(--border-color)] text-white rounded-lg text-sm font-medium hover:bg-white/5"
                onClick={handleOpenSettings}
                aria-label="Open cookie settings"
              >
                Settings
              </button>
              <button
                className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark"
                onClick={handleAcceptAll}
                aria-label="Accept all cookies"
              >
                Accept All
              </button>
            </div>
          </div>

          <div className="max-w-6xl mx-auto mt-3">
            <a href="/cookie-policy" className="text-xs text-primary hover:underline">
              Cookie Policy
            </a>
          </div>
        </div>
      )}

      {showSettings && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className="bg-[var(--card-bg)] rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            role="dialog"
            aria-label="Cookie Settings"
          >
            <div className="flex items-center justify-between p-5 border-b border-[var(--border-color)]">
              <h2 className="text-xl font-bold text-white">Cookie Preferences</h2>
              <button
                className="w-8 h-8 flex items-center justify-center bg-transparent border-none text-muted hover:text-white rounded-md hover:bg-white/5"
                onClick={() => setShowSettings(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="p-5">
              <p className="text-muted mb-6">
                We use cookies to personalize your experience and analyze site
                traffic. You can choose which cookies to accept below.
              </p>

              <div className="space-y-5">
                <div className="p-4 bg-white/5 rounded-lg border border-[var(--border-color)]">
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center gap-3 cursor-not-allowed">
                      <input
                        type="checkbox"
                        checked={preferences.functional}
                        disabled
                        className="w-4 h-4 rounded"
                        aria-label="Functional cookies (always enabled)"
                      />
                      <span className="font-semibold text-white">
                        Functional Cookies
                      </span>
                    </label>
                    <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded">Required</span>
                  </div>
                  <p className="text-sm text-muted mt-2">
                    Essential for the website to function properly. These cookies
                    remember your authentication, preferences, and keep your
                    session active.
                  </p>
                </div>

                <div className="p-4 bg-white/5 rounded-lg border border-[var(--border-color)]">
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) =>
                          setPreferences({
                            ...preferences,
                            analytics: e.target.checked,
                          })
                        }
                        className="w-4 h-4 rounded"
                        aria-label="Analytics cookies"
                      />
                      <span className="font-semibold text-white">
                        Analytics Cookies
                      </span>
                    </label>
                  </div>
                  <p className="text-sm text-muted mt-2">
                    Help us understand how you use the site. We use Google
                    Analytics to track page views, user behavior, and performance
                    metrics. No personal data is collected.
                  </p>
                </div>

                <div className="p-4 bg-white/5 rounded-lg border border-[var(--border-color)]">
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={(e) =>
                          setPreferences({
                            ...preferences,
                            marketing: e.target.checked,
                          })
                        }
                        className="w-4 h-4 rounded"
                        aria-label="Marketing cookies"
                      />
                      <span className="font-semibold text-white">
                        Marketing Cookies
                      </span>
                    </label>
                  </div>
                  <p className="text-sm text-muted mt-2">
                    Used to track you across websites and display personalized
                    ads. These are optional and can be disabled at any time.
                  </p>
                </div>
              </div>

              <p className="text-xs text-muted mt-5">
                You can change your cookie preferences at any time from the
                Settings page or by clearing your browser's local storage.
              </p>
            </div>

            <div className="flex gap-3 p-5 border-t border-[var(--border-color)]">
              <button
                className="flex-1 px-4 py-2.5 bg-transparent border border-[var(--border-color)] text-white rounded-lg font-medium hover:bg-white/5"
                onClick={() => setShowSettings(false)}
              >
                Cancel
              </button>
              <button 
                className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark" 
                onClick={handleSavePreferences}
              >
                Save Preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default CookieConsent;
